import Vue from 'vue';
import gql from 'graphql-tag';
import { getProducts } from '@breakmedia/prime-vue-product/src/store/getProducts';
import { Observable } from 'rxjs';
import { first } from 'rxjs/operators';
import VueCookies from 'vue-cookies';

const paidCoupons = Vue.$session.get('paidCoupons');

Vue.use(VueCookies);

export default {
    namespaced: true,
    state: {
        couponCodes: [],
        error: null,
        displayCouponModal: false,
        paidCoupons: paidCoupons ? paidCoupons : {},
        loading: false,
    },
    getters: {
        getDisplayCouponModal: state => {
            return state.displayCouponModal;
        },
        paidCouponsAmount: state => {
            return Object.values(state.paidCoupons).reduce((a, b) => a + b, 0);
        },
    },
    mutations: {
        setError(state, val) {
            state.error = val;
        },
        setDisplayCouponModal(state, value) {
            state.displayCouponModal = value;
        },
        setPaidCoupons(state, value) {
            state.paidCoupons = {
                ...value,
            };
        },
        addPaidCoupon(state, { code, amount }) {
            state.paidCoupons = {
                ...state.paidCoupons,
                [code]: amount,
            };

            Vue.$session.set('paidCoupons', state.paidCoupons);
            Vue.$order.clearOrder();
        },
        removePaidCoupon(state, { coupon }) {
            state.paidCoupons = Object.fromEntries(
                Object.entries(state.paidCoupons).filter(([key]) => key !== coupon),
            );
            Vue.$session.set('paidCoupons', state.paidCoupons);
            Vue.$order.clearOrder();
        },
        setLoading: (state, value) => (state.loading = value),
        setCouponCodes: (state, value) => (state.couponCodes = value),
        addCouponCode(state, value) {
            if (!state.couponCodes.includes(value)) {
                state.couponCodes.push(value);
            }
        },
        removeCouponCode(state, value) {
            state.couponCodes = state.couponCodes.filter(c => c !== value);
            Vue.$order.clearOrder();
        },
    },
    actions: {
        async getCouponData({ state }, { coupon, prepaid = false, email = null, couponsCount }) {
            const basketTotal = await Vue.$basket.getBasketTotalAsync();

            couponsCount = couponsCount || [coupon, ...state.couponCodes.filter(c => c !== coupon)].length;

            return Vue.$graphql
                .mutate({
                    mutation: gql`
                        mutation(
                            $coupon: String!
                            $basketTotal: Float!
                            $countryId: Int
                            $currency: String!
                            $email: String
                            $basket: BasketInput
                            $prepaid: Boolean
                            $appliedWithMultipleCoupons: Boolean
                        ) {
                            applyCoupon(
                                coupon: $coupon
                                basketTotal: $basketTotal
                                countryId: $countryId
                                currency: $currency
                                email: $email
                                basket: $basket
                                prepaid: $prepaid
                                appliedWithMultipleCoupons: $appliedWithMultipleCoupons
                            ) {
                                code
                                isAbsolute
                                amount
                                pageId
                                boundProducts
                                minBasketAmount
                                products
                                redeemAssignedProductsOnly
                                subtractFromRrp
                                includeShippingCost
                                errorPage
                            }
                        }
                    `,
                    variables: {
                        coupon,
                        basketTotal: basketTotal || 0,
                        countryId: Vue.$country.getCountryID(),
                        currency: Vue.$currency.getSelected(),
                        email: (Vue.$store.state.Registration && Vue.$store.state.Registration.email) || email,
                        basket: {
                            ...Vue.$basket.getBasket(),
                            items: Object.values(Vue.$basket.getBasket().items),
                        },
                        prepaid,
                        appliedWithMultipleCoupons: prepaid ? false : couponsCount > 1,
                    },
                    fetchPolicy: 'no-cache',
                })
                .then(r => r.data.applyCoupon);
        },
        applyCoupons({ state, dispatch }, options) {
            state.couponCodes.forEach(couponCode => {
                dispatch('applyCoupon', {
                    ...options,
                    couponCode,
                });
            });
        },
        async applyCoupon({ state, commit, dispatch }, options) {
            const couponCode = options && options.couponCode;
            const basketTotal = await Vue.$basket.getBasketTotalAsync();
            const silent = options.silent;

            if (!state.couponCodes.includes(couponCode) && basketTotal <= 0) {
                Vue.$coupon.addFailedCoupon(couponCode);
                dispatch('removeCoupon', { couponCode });

                return Promise.resolve();
            }

            if (!couponCode) {
                if (silent !== true) {
                    commit('setError', Vue.$language.translate('Coupon code is empty'));
                }

                return Promise.resolve();
            }

            if (silent !== true) {
                commit('setLoading', true);
            }

            return dispatch('getCouponData', {
                coupon: couponCode,
                email: options && options.email,
            })
                .then(result => {
                    if (result && (result.amount || result.boundProducts.length > 0)) {
                        return new Promise(resolve => {
                            if (result.boundProducts.length > 0) {
                                getProducts(
                                    new Observable(subscriber =>
                                        subscriber.next({
                                            ids: result.boundProducts,
                                            skipPriceParams: false,
                                        }),
                                    ),
                                )
                                    .pipe(first())
                                    .subscribe(products => {
                                        products.forEach(product => {
                                            product.boundCoupon = couponCode;
                                            dispatch(
                                                'Product/buyProduct',
                                                {
                                                    product: product,
                                                    currentProductId: product.id,
                                                    currentMerchantId: Object.keys(product.merchant)[0],
                                                    quantity: 1,
                                                    silent: true,
                                                },
                                                { root: true },
                                            );
                                        });

                                        resolve(result);
                                    });
                            } else {
                                resolve(result);
                            }
                        });
                    } else if (silent !== true) {
                        commit('setError', Vue.$language.translate('Something went wrong.'));
                    }
                })
                .then(result => {
                    if (result) {
                        if (state.couponCodes.length === 1 && !state.couponCodes.includes(couponCode)) {
                            //if there is already a coupon check if it's allowed with other coupons.
                            dispatch('getCouponData', {
                                coupon: state.couponCodes[0],
                                email: options && options.email,
                                couponsCount: 2,
                            })
                                .then(() => {
                                    dispatch('addCoupon', result);
                                })
                                .catch(e => {
                                    if (options.prioritizeNewCoupon) {
                                        dispatch('removeCoupon', { couponCode: state.couponCodes[0] });
                                    } else {
                                        dispatch('removeCoupon', { couponCode });
                                        if (e.graphQLErrors && e.graphQLErrors.length && silent !== true) {
                                            commit(
                                                'setError',
                                                Vue.$language.translate(e.graphQLErrors[0].message.message),
                                            );
                                        }
                                    }
                                });
                        } else {
                            dispatch('addCoupon', result);
                        }
                    }

                    if (silent !== true) {
                        if (result && result.message) {
                            commit('setError', result.message);
                        } else {
                            commit('setError', '');
                        }
                    }

                    return result;
                })
                .catch(e => {
                    if (e.graphQLErrors && e.graphQLErrors.length) {
                        const error = e.graphQLErrors[0];

                        if (error.message.errorCode === 101 || error.message.errorCode === 105) {
                            Vue.$coupon.addFailedCoupon(couponCode);
                        }

                        if (options.prioritizeNewCoupon && error.message.errorCode === 100) {
                            state.couponCodes.map(couponCode => dispatch('removeCoupon', { couponCode }));
                            dispatch('addCoupon', { couponCode });
                        } else {
                            dispatch('removeCoupon', { couponCode });
                        }

                        if (silent !== true) {
                            if (
                                error.extensions &&
                                error.extensions.exception &&
                                error.extensions.exception.status === 409
                            ) {
                                const countryName = Vue.$country.getCountryNameBy('id', Vue.$country.getCountryID());
                                const err = Vue.$language.translate(
                                    'Der Gutschein ist in %value% nicht gültig und ' +
                                        'konnte daher nicht angewendet werden.',
                                );
                                commit('setError', err.replace('%value%', Vue.$language.translate(countryName)));
                            } else if (e.graphQLErrors[0].message && e.graphQLErrors[0].message.message) {
                                commit('setError', Vue.$language.translate(error.message.message));
                            }
                        }

                        return error.message;
                    } else if (silent !== true) {
                        commit('setError', Vue.$language.translate('Coupon was not found.'));
                    }
                })
                .finally(() => commit('setLoading', false));
        },
        addCoupon({ state, commit }, coupon) {
            commit('addCouponCode', coupon.code);

            Vue.$basket.addBasketOption({
                key: 'coupon',
                value: state.couponCodes,
            });

            Vue.$coupon.applyDiscount(coupon);
            Vue.$order.clearOrder();
        },
        removeCoupon({ state, commit }, { couponCode }) {
            commit('removeCouponCode', couponCode);

            Vue.$basket.removeBasketOption('coupon');

            Vue.$basket.addBasketOption({
                key: 'coupon',
                value: state.couponCodes,
            });

            Vue.$basket.addDiscount({
                key: `coupon-${couponCode}`,
                value: null,
            });

            const basketItems = Vue.$basket.getBasketItems();
            for (const basketItem of Object.values(basketItems)) {
                if (basketItem.options.hasOwnProperty('boundCoupon') && basketItem.options.boundCoupon === couponCode) {
                    Vue.$basket.removeBasketItemLocally(basketItem);
                }
            }
            Vue.$order.clearOrder();
        },
        removePaidCoupon({ commit }, { couponCode }) {
            commit('removePaidCoupon', couponCode);
        },
    },
};
